import { Autocomplete, AutocompleteItem, Input } from '@heroui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { isValidBBAN, isValidBIC } from 'ibantools'
import { useTranslate } from '@/hooks'
import { useCountries } from '@/hooks/use-countries'
import { useAppSelector } from '@/redux/hooks'

const AU_BSB_REGEX = /^([0-9]{3})(-| )?([0-9]{3}),*$/
const US_ROUTING_REGEX = /^([0-9]{9}),*$/

export const internationalSchema = z
  .object({
    country_code: z.string().min(1, { message: 'country_code_min_char' }),
    swift: z.string(),
    bban: z.string().min(1, { message: 'bban_min_char' })
  })
  .superRefine(({ country_code, swift, bban }, ctx) => {
    if (country_code === 'other') {
      if (!isValidBIC(swift)) {
        ctx.addIssue({
          code: 'custom',
          message: 'iban_swift_hint',
          path: ['swift']
        })

        return
      }

      if (!isValidBBAN(bban, swift.substring(4, 6))) {
        ctx.addIssue({
          code: 'custom',
          message: 'iban_bban_hint',
          path: ['bban']
        })
      }
    }

    if (country_code === 'US') {
      if (!US_ROUTING_REGEX.test(swift)) {
        ctx.addIssue({
          code: 'custom',
          message: 'iban_swift_hint',
          path: ['swift']
        })
      }
    }

    if (country_code === 'AU') {
      if (!AU_BSB_REGEX.test(swift)) {
        ctx.addIssue({
          code: 'custom',
          message: 'iban_swift_hint',
          path: ['swift']
        })
      }
    }
  })

export type InternationalFormData = z.infer<typeof internationalSchema>

interface InternationalFormProps {
  onSubmit: (data: InternationalFormData) => void
}

export const InternationalForm = ({ onSubmit }: InternationalFormProps) => {
  const { translate } = useTranslate()

  const ecommerceId = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_id
  )

  const {
    register,
    control,
    resetField,
    handleSubmit,
    formState: { errors }
  } = useForm<InternationalFormData>({
    resolver: zodResolver(internationalSchema),
    mode: 'onBlur',
    defaultValues: { country_code: '', swift: '', bban: '' }
  })

  const countryCode = useWatch({ control, name: 'country_code' })

  const { data } = useCountries({ ecommerceID: ecommerceId })

  const allowedCountries =
    data
      ?.filter((country) => country.iso === 'AU' || country.iso === 'US')
      .concat({ nicename: 'Other', iso: 'other' }) ?? []

  return (
    <form
      id="bank-transfer-form"
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-3"
    >
      <Controller
        control={control}
        name="country_code"
        render={({ field: { onChange, value } }) => (
          <Autocomplete
            id="country-select"
            radius="sm"
            size="sm"
            variant="bordered"
            label={translate('iban_bank_country')}
            selectedKey={value ?? null}
            onSelectionChange={(key) => {
              resetField('swift')
              resetField('bban')
              onChange(key)
            }}
            isInvalid={!!errors.country_code}
            errorMessage={
              errors.country_code?.message
                ? translate(errors.country_code.message)
                : undefined
            }
            className="[&_[data-slot=input-wrapper]]:border-solid"
          >
            {allowedCountries.map((country) => (
              <AutocompleteItem key={country.iso}>
                {country.nicename}
              </AutocompleteItem>
            ))}
          </Autocomplete>
        )}
      />
      <div className="flex gap-2">
        <Controller
          control={control}
          name="swift"
          render={({ field: { onChange, value, onBlur } }) => (
            <Input
              id="SWIFT"
              onBlur={onBlur}
              value={value}
              onChange={(e) => {
                if (countryCode === 'AU') {
                  const bsb =
                    e.currentTarget.value
                      .replace(/[-]/g, '')
                      .match(/.{1,3}/g)
                      ?.join('-') ?? ''
                  onChange(bsb)
                } else {
                  onChange(e.currentTarget.value)
                }
              }}
              label={translate(
                `iban_introduce_${countryCode ? countryCode : ''}`
              )}
              radius="sm"
              size="sm"
              variant="bordered"
              classNames={{
                inputWrapper: 'border-solid'
              }}
              isDisabled={countryCode?.length === 0}
              isInvalid={!!errors.swift}
              errorMessage={
                errors.swift?.message
                  ? translate(errors.swift.message)
                  : undefined
              }
            />
          )}
        />
        <Input
          {...register('bban')}
          id="BBAN"
          label={translate('iban_introduce_bban')}
          radius="sm"
          size="sm"
          variant="bordered"
          isInvalid={!!errors.bban}
          errorMessage={
            errors.bban?.message ? translate(errors.bban.message) : undefined
          }
          classNames={{
            inputWrapper: 'border-solid'
          }}
          isDisabled={countryCode?.length === 0}
        />
      </div>
    </form>
  )
}
